import type { ReactNode } from 'react';
import { SortableHeader } from '@/components/primitives/SortableHeader';
import styles from './DataTable.module.css';

interface Column<TRow, K extends string = string> {
  key: K;
  header: string;
  sortable?: boolean;
  align?: 'left' | 'right';
  width?: string;
  render: (row: TRow) => ReactNode;
}

interface Sort<K extends string = string> {
  key: K;
  direction: 'asc' | 'desc';
}

interface DataTableProps<TRow, K extends string = string> {
  caption: string;
  columns: Column<TRow, K>[];
  rows: TRow[];
  getRowKey: (row: TRow) => string;
  sort?: Sort<K>;
  onSort?: (key: K) => void;
  footer?: ReactNode;
  emptyState?: ReactNode;
  zebra?: boolean;
  onRowClick?: (row: TRow) => void;
  rowHref?: (row: TRow) => string;
}

export function DataTable<TRow, K extends string = string>({
  caption,
  columns,
  rows,
  getRowKey,
  sort,
  onSort,
  footer,
  emptyState,
  zebra = false,
  onRowClick,
  rowHref,
}: DataTableProps<TRow, K>) {
  const isEmpty = rows.length === 0;

  return (
    <div className={styles.wrapper} data-testid="data-table">
      <table className={styles.table}>
        <caption className={styles.caption}>{caption}</caption>
        <thead>
          <tr>
            {columns.map((col) =>
              col.sortable && onSort ? (
                <SortableHeader
                  key={col.key}
                  label={col.header}
                  sortKey={col.key}
                  currentSort={sort}
                  onSort={onSort}
                  align={col.align}
                  width={col.width}
                />
              ) : (
                <th
                  key={col.key}
                  className={styles.th}
                  style={{ width: col.width, textAlign: col.align ?? 'left' }}
                >
                  {col.header}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {isEmpty && emptyState ? (
            <tr>
              <td colSpan={columns.length} className={styles.emptyCell}>
                {emptyState}
              </td>
            </tr>
          ) : (
            rows.map((row, rowIdx) => {
              const key = getRowKey(row);
              const clickable = !!(onRowClick || rowHref);
              const rowCls = [
                styles.row,
                zebra && rowIdx % 2 === 1 ? styles.zebra : '',
                clickable ? styles.clickable : '',
              ]
                .filter(Boolean)
                .join(' ');

              const handleClick = onRowClick ? () => onRowClick(row) : undefined;

              return (
                <tr key={key} className={rowCls} onClick={handleClick}>
                  {columns.map((col, colIdx) => {
                    const cell = col.render(row);
                    // If rowHref is provided, wrap first column in a link
                    if (rowHref && colIdx === 0) {
                      return (
                        <td
                          key={col.key}
                          className={styles.td}
                          style={{ textAlign: col.align ?? 'left' }}
                        >
                          <a href={rowHref(row)} className={styles.rowLink}>
                            {cell}
                          </a>
                        </td>
                      );
                    }
                    return (
                      <td
                        key={col.key}
                        className={styles.td}
                        style={{ textAlign: col.align ?? 'left' }}
                      >
                        {cell}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
        {footer && <tfoot className={styles.footer}>{footer}</tfoot>}
      </table>
    </div>
  );
}
